/**
 * THE FIRM ADMIN'S AGREEMENT GROUP, `cutoverAgreementViews` at
 * `/api/bms/cutover-agreement-views`, on the internal session lane only
 * (cutover plan ruling 28; note 01 E.1, E.2; research 01 §2.3.6, §2.3.7;
 * api-conventions §7).
 *
 * BROWSER-SAFE. The settings slice and the root banner import THIS module to
 * build their transport, never the leaf's barrel, which publishes a
 * repository that reaches `@effect/sql-pg`. Kit imports are relative for the
 * same reason.
 *
 * INTERNAL ONLY, NEVER PUBLISHED. An agreement is the firm administrator's
 * signed act in the app, never a firm key's: there is no `/api/v1` twin. THIS
 * BUILD ADDS NO RPC GROUP (CLAUDE.md rule 3, BMS exception).
 *
 * FIVE ENDPOINTS. The status read is cheap (the migration row and the latest
 * agreement's counts, no matcher), because the root banner asks it for every
 * administrator of a legacy firm. The proposal the page resolves is the
 * matcher's own read (`cutoverMatcherViews`), not repeated here. The write
 * agrees, and answers the new status.
 *
 * THE MATCH DRAFT (decision 041): the administrator's answers saved as they
 * are made, so the matches can be worked over several sittings. One read of
 * the whole draft, one write per answer (insert or replace the legacy
 * client's answer) and one clear. Nothing moves from the draft: `agree` still
 * carries the resolution the page builds over it.
 *
 * THE FIRM IS IN THE PATH and must be the caller's own: another firm's id
 * answers not found, as if it did not exist. The caller must be the firm's
 * administrator; anyone else is refused 403 before anything is read.
 */
import { HttpApi, HttpApiEndpoint, HttpApiGroup, OpenApi } from "@effect/platform"
import { Schema as S } from "effect"

// RELATIVE, not the public-api barrel: see the header.
import { ApiConflictWire } from "../../../../public-api/domain/public-api-error"
import { withBmsErrors } from "../../../shared/presentation/errors"
import { BmsSessionMiddleware } from "../../../shared/presentation/session-middleware"
import { AgreePayload, AgreementStatus } from "../domain/agreement"
import { ClearedMatchChoice, MatchDraft, MatchDraftRow, SaveMatchChoicePayload } from "../domain/match-draft"

export const AgreementPath = S.Struct({ organizationId: S.UUID })

export const getAgreementStatusEndpoint = HttpApiEndpoint.get("getAgreementStatus", "/organizations/:organizationId/status")
  .setPath(AgreementPath)
  .addSuccess(AgreementStatus)
  .annotate(OpenApi.Identifier, "getAgreementStatus")
  .annotate(OpenApi.Summary, "Read where the firm's move to Cova BMS stands")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. Whether support has requested the move, the firm's state, its latest agreement as counts, and whether an agreement can be given now.",
  )

export const agreeEndpoint = HttpApiEndpoint.post("agree", "/organizations/:organizationId/agreement")
  .setPath(AgreementPath)
  .setPayload(AgreePayload)
  .addSuccess(AgreementStatus)
  .addError(ApiConflictWire)
  .annotate(OpenApi.Identifier, "agreeToMove")
  .annotate(OpenApi.Summary, "Agree to move the firm's clients to Cova BMS")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. Records the agreement, the scope and one decision per client in the firm's old client list, checked against the current match proposal. Refused with a conflict when the firm already agreed or its move has started.",
  )

export const MatchDraftPath = S.Struct({ organizationId: S.UUID, legacyId: S.UUID })

export const getMatchDraftEndpoint = HttpApiEndpoint.get("getMatchDraft", "/organizations/:organizationId/draft")
  .setPath(AgreementPath)
  .addSuccess(MatchDraft)
  .annotate(OpenApi.Identifier, "getMatchDraft")
  .annotate(OpenApi.Summary, "Read the firm administrator's saved client match answers")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. One saved answer per legacy client answered so far, with who answered and when, so the matches can be worked over several sittings.",
  )

export const saveMatchChoiceEndpoint = HttpApiEndpoint.post("saveMatchChoice", "/organizations/:organizationId/draft/:legacyId")
  .setPath(MatchDraftPath)
  .setPayload(SaveMatchChoicePayload)
  .addSuccess(MatchDraftRow)
  .annotate(OpenApi.Identifier, "saveMatchChoice")
  .annotate(OpenApi.Summary, "Save the administrator's answer for one client match")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. Saves join, keep apart or decide later for one legacy client, replacing its last answer. Nothing moves until the administrator agrees.",
  )

export const clearMatchChoiceEndpoint = HttpApiEndpoint.post("clearMatchChoice", "/organizations/:organizationId/draft/:legacyId/clear")
  .setPath(MatchDraftPath)
  .addSuccess(ClearedMatchChoice)
  .annotate(OpenApi.Identifier, "clearMatchChoice")
  .annotate(OpenApi.Summary, "Clear the administrator's saved answer for one client match")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. The legacy client reads as never answered again.",
  )

export const CutoverAgreementViewsGroup = withBmsErrors(
  HttpApiGroup.make("cutoverAgreementViews")
    .add(getAgreementStatusEndpoint)
    .add(agreeEndpoint)
    .add(getMatchDraftEndpoint)
    .add(saveMatchChoiceEndpoint)
    .add(clearMatchChoiceEndpoint),
)
  .middleware(BmsSessionMiddleware)
  .prefix("/api/bms/cutover-agreement-views")

export class CutoverAgreementViewsApi extends HttpApi.make("BmsCutoverAgreementViewsApi").add(CutoverAgreementViewsGroup) {}
